import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { useGameStore } from '../../store/gameStore';
import { Clock, Skull, User, MapPin, FileText, ArrowRight, RotateCcw, Search, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { isInterviewAction, getNormalizedActionType, isLocationAction } from '../../utils/actionHelpers';

export const Timeline = ({ open, onClose }) => {
  const {
    selectedTheme,
    caseData,
    messageHistory,
    discovered,
    clues,
    suspects,
    locations,
    examinations,
    interrogationDialogues,
  } = useGameStore();

  // Helper to get location name from ID
  const getLocationName = (locationId) => {
    if (!locationId) return null;
    const location = locations?.find(l => l.id === locationId);
    return location?.name || locationId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const [viewMode, setViewMode] = useState('vertical'); // 'vertical' or 'horizontal'

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  // Build timeline events from discovered information
  const buildTimelineEvents = () => {
    const events = [];

    // Add murder event (always first, if we have victim info)
    if (caseData?.victim) {
      events.push({
        id: 'murder',
        type: 'murder',
        title: 'Murder Committed',
        description: `${caseData.victim.name} was killed`,
        time: caseData.victim.time_of_death || 'Time unknown',
        location: caseData.victim.location_found || 'Crime scene',
        icon: Skull,
        color: '#ef4444',
        priority: 0,
      });
    }

    // Add events from clues that have temporal information (case timeline)
    const discoveredClues = (clues || []).filter(c => discovered?.clues?.includes(c.id));
    discoveredClues.forEach((clue, idx) => {
      if (clue.time_reference || clue.when_found) {
        events.push({
          id: `clue-${clue.id}`,
          type: 'clue',
          title: clue.name,
          description: clue.description,
          time: clue.time_reference || clue.when_found || 'Unknown time',
          location: clue.location,
          icon: FileText,
          color: '#eab308',
          priority: 10 + idx,
        });
      }
    });

    // Add examination discovery events (investigation timeline)
    // Shows what was discovered where and when during player's investigation
    if (examinations) {
      Object.entries(examinations).forEach(([locationId, examList]) => {
        const locationName = getLocationName(locationId);
        examList.forEach((exam, examIdx) => {
          // Add event for each clue discovered
          if (exam.clues_found?.length > 0) {
            exam.clues_found.forEach((clueId) => {
              const clue = (clues || []).find(c => c.id === clueId);
              if (clue) {
                events.push({
                  id: `discovery-${clueId}-${exam.turn}`,
                  type: 'discovery',
                  title: `Found: ${clue.name}`,
                  description: `Turn ${exam.turn} • Examined ${locationName}`,
                  time: `Turn ${exam.turn}`,
                  location: locationName,
                  icon: Search,
                  color: '#22c55e', // green for discoveries
                  priority: 200 + (exam.turn * 10) + examIdx,
                  source: 'examine',
                  effectiveness: exam.effectiveness,
                });
              }
            });
          }
        });
      });
    }

    // Add interrogation discovery events
    // Check messageHistory for interrogation actions with revealed info
    const interrogationMessages = messageHistory.filter(
      msg => getNormalizedActionType(msg.action) === 'interrogation' && msg.revealedInfo
    );
    interrogationMessages.forEach((msg, idx) => {
      const suspect = (suspects || []).find(s => s.id === msg.target);
      const suspectName = suspect?.name || 'Unknown';
      const turnNum = msg.turnNumber || idx + 1;

      // Add events for clues revealed during interrogation
      if (msg.revealedInfo?.new_clues?.length > 0) {
        msg.revealedInfo.new_clues.forEach((clueId) => {
          const clue = (clues || []).find(c => c.id === clueId);
          if (clue) {
            events.push({
              id: `interrog-clue-${clueId}-${turnNum}`,
              type: 'discovery',
              title: `Learned: ${clue.name}`,
              description: `Turn ${turnNum} • Interrogated ${suspectName}`,
              time: `Turn ${turnNum}`,
              location: null,
              icon: MessageSquare,
              color: '#a855f7', // purple for interrogation reveals
              priority: 300 + turnNum * 10,
              source: 'interrogation',
            });
          }
        });
      }
    });

    // Add interview/interrogation events
    const interviews = messageHistory.filter(msg => isInterviewAction(msg.action) && msg.narrative);
    interviews.forEach((interview, idx) => {
      const suspect = (suspects || []).find(s => s.id === interview.target);
      if (suspect) {
        const actionType = getNormalizedActionType(interview.action);
        const isInterrogation = actionType === 'interrogation';
        events.push({
          id: `interview-${idx}`,
          type: isInterrogation ? 'interrogation' : 'interview',
          title: isInterrogation ? `Interrogated ${suspect.name}` : `Interviewed ${suspect.name}`,
          description: suspect.alibi ? `Claimed: ${suspect.alibi.substring(0, 100)}...` : (isInterrogation ? 'Formal interrogation conducted' : 'Interview conducted'),
          time: `Turn ${interview.turnNumber || idx + 1}`,
          location: 'Investigation',
          icon: User,
          color: themeAccent,
          priority: 100 + idx,
        });
      }
    });

    // Add location discoveries (using shared utility)
    const locationEvents = messageHistory.filter(msg => isLocationAction(msg.action));
    const seenLocations = new Set();
    locationEvents.forEach((event, idx) => {
      if (event.target && !seenLocations.has(event.target)) {
        seenLocations.add(event.target);
        const locationName = getLocationName(event.target);
        const actionType = getNormalizedActionType(event.action);
        events.push({
          id: `location-${event.target}`,
          type: 'location',
          title: actionType === 'travel' ? `Traveled to ${locationName}` : `Examined ${locationName}`,
          description: `Turn ${event.turnNumber || idx + 1}`,
          time: `Turn ${event.turnNumber || idx + 1}`,
          location: null, // Don't show redundant location tag
          icon: MapPin,
          color: '#3b82f6',
          priority: 50 + idx,
        });
      }
    });

    // Sort by priority (murder first, then chronologically)
    return events.sort((a, b) => a.priority - b.priority);
  };

  const events = buildTimelineEvents();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl lg:max-w-6xl h-[80vh] lg:h-[90vh] bg-gradient-to-br from-slate-900 to-slate-800 border-2" style={{ borderColor: themeAccent }}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <Clock className="w-6 h-6" style={{ color: themeAccent }} />
              Timeline Reconstruction
            </DialogTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={viewMode === 'vertical' ? 'default' : 'outline'}
                onClick={() => setViewMode('vertical')}
                className={viewMode === 'vertical' ? '' : 'bg-black/40 border-white/20 text-white'}
                style={viewMode === 'vertical' ? { backgroundColor: themeAccent } : {}}
              >
                Vertical
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'horizontal' ? 'default' : 'outline'}
                onClick={() => setViewMode('horizontal')}
                className={viewMode === 'horizontal' ? '' : 'bg-black/40 border-white/20 text-white'}
                style={viewMode === 'horizontal' ? { backgroundColor: themeAccent } : {}}
              >
                Horizontal
              </Button>
            </div>
          </div>
        </DialogHeader>

        {events.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No timeline events yet</p>
              <p className="text-sm mt-2">Investigate the case to reconstruct the timeline</p>
            </div>
          </div>
        ) : viewMode === 'vertical' ? (
          /* Vertical Timeline */
          <ScrollArea className="flex-1 pr-4">
            <div className="relative">
              {/* Timeline line */}
              <div
                className="absolute left-6 top-0 bottom-0 w-0.5"
                style={{ backgroundColor: `${themeAccent}40` }}
              />

              {/* Events */}
              <div className="space-y-6 pl-16 py-4">
                {events.map((event, idx) => {
                  const Icon = event.icon;
                  return (
                    <div key={event.id} className="relative">
                      {/* Timeline dot */}
                      <div
                        className="absolute -left-[52px] top-2 w-4 h-4 rounded-full border-2 flex items-center justify-center"
                        style={{
                          backgroundColor: event.color,
                          borderColor: event.color,
                        }}
                      >
                        {idx === 0 && (
                          <div className="absolute w-6 h-6 rounded-full animate-ping opacity-30" style={{ backgroundColor: event.color }} />
                        )}
                      </div>

                      {/* Event card */}
                      <div className="bg-black/30 border border-white/10 rounded-lg p-4 hover:bg-black/40 transition-colors">
                        <div className="flex items-start gap-3">
                          <div
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: `${event.color}20` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: event.color }} />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-semibold">{event.title}</h3>
                            <p className="text-gray-400 text-sm mt-1">{event.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {event.time}
                              </span>
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {event.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </ScrollArea>
        ) : (
          /* Horizontal Timeline */
          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <div className="flex items-center gap-4 p-4 min-w-max h-full">
              {events.map((event, idx) => {
                const Icon = event.icon;
                return (
                  <React.Fragment key={event.id}>
                    {/* Event card */}
                    <div className="relative flex flex-col items-center w-48">
                      {/* Dot */}
                      <div
                        className="w-4 h-4 rounded-full mb-3"
                        style={{ backgroundColor: event.color }}
                      />

                      {/* Card */}
                      <div className="bg-black/30 border border-white/10 rounded-lg p-3 text-center w-full">
                        <Icon className="w-5 h-5 mx-auto mb-2" style={{ color: event.color }} />
                        <h3 className="text-white font-semibold text-sm truncate">{event.title}</h3>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{event.description}</p>
                        <p className="text-xs text-gray-500 mt-2">{event.time}</p>
                        {event.location && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Connector arrow */}
                    {idx < events.length - 1 && (
                      <ArrowRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-2 border-t border-white/10">
          <p className="text-xs text-gray-400 text-center">
            Timeline reconstructed from {events.length} discovered events
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Timeline;
